/* Lavora con noi: ricerca posizioni e invio candidature. */

// Filtra l'elenco di tutte le posizioni (già nella pagina) mentre si digita.
class CareersSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.items = [...this.querySelectorAll('[data-careers-job]')];
    this.list = this.querySelector('.careers-search__results');
    this.empty = this.querySelector('.careers-search__empty');
    const normalizza = (s) =>
      s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    this.items.forEach((li) => (li.dataset.testo = normalizza(li.textContent)));
    this.input.addEventListener('input', () => {
      const parole = normalizza(this.input.value.trim()).split(/\s+/).filter(Boolean);
      const attiva = parole.join('').length >= 2;
      let trovate = 0;
      this.items.forEach((li) => {
        const ok = attiva && parole.every((p) => li.dataset.testo.includes(p));
        li.hidden = !ok;
        if (ok) trovate++;
      });
      this.list.hidden = !attiva;
      this.empty.hidden = !attiva || trovate > 0;
    });
  }
}
customElements.define('careers-search', CareersSearch);

// Elenco posizioni a pagine (data-per-pagina): l'ordine e i filtri arrivano già da Liquid.
class CareersPages extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.careers-jobs > li')];
    this.per = Number(this.dataset.perPagina) || 10;
    this.totale = Math.ceil(this.items.length / this.per);
    this.nav = this.querySelector('.careers-pages');
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.stato = this.querySelector('[data-stato]');
    this.prev.addEventListener('click', () => this.mostra(this.pagina - 1, true));
    this.next.addEventListener('click', () => this.mostra(this.pagina + 1, true));
    this.nav.hidden = this.totale < 2;
    this.mostra(0);
  }
  mostra(pagina, scorri) {
    this.pagina = Math.max(0, Math.min(pagina, this.totale - 1));
    this.items.forEach((li, i) => (li.hidden = Math.floor(i / this.per) !== this.pagina));
    this.prev.disabled = this.pagina === 0;
    this.next.disabled = this.pagina >= this.totale - 1;
    this.stato.textContent = `${this.pagina + 1} / ${this.totale}`;
    if (scorri) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
customElements.define('careers-pages', CareersPages);

// Invio della candidatura al server dell'app (multipart, con i file).
class CareersForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.message = this.querySelector('.careers-form__message');
    this.button = this.form.querySelector('button[type="submit"]');
    // tempo di compilazione misurato qui (non l'orario: l'orologio del PC può essere sbagliato)
    this.aperto = performance.now();
    this.form.addEventListener('submit', (e) => this.invia(e));
    this.button.disabled = false;
    this.form.addEventListener('input', (e) =>
      e.target.closest('[data-campo]')?.removeAttribute('data-errore')
    );
  }

  testo(chiave) {
    return this.dataset[chiave] || '';
  }

  mostra(testo, tipo = 'errore') {
    this.message.textContent = testo;
    this.message.dataset.tipo = tipo;
    this.message.hidden = !testo;
  }

  segnaErrori(ids) {
    this.querySelectorAll('[data-errore]').forEach((el) => el.removeAttribute('data-errore'));
    ids.forEach((id) => this.querySelector(`[data-campo="${CSS.escape(id)}"]`)?.setAttribute('data-errore', ''));
    this.querySelector('[data-errore]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // Stessi controlli del server, per dare subito un riscontro al candidato.
  controlla() {
    const errate = [];
    this.querySelectorAll('[data-campo]').forEach((campo) => {
      const input = campo.querySelector('input, textarea, select');
      if (!input) return;
      if (input.type === 'file') {
        const f = input.files[0];
        if ((campo.dataset.obbligatoria === 'true' && !f) || (f && f.size > 10 * 1024 * 1024)) {
          errate.push(campo.dataset.campo);
        }
        return;
      }
      const valori = [...campo.querySelectorAll('input, textarea, select')].filter((el) =>
        el.type === 'checkbox' || el.type === 'radio' ? el.checked : el.value.trim()
      );
      if (campo.dataset.obbligatoria === 'true' && valori.length === 0) errate.push(campo.dataset.campo);
      else if (valori.length && !valori.every((el) => el.checkValidity())) errate.push(campo.dataset.campo);
    });
    return errate;
  }

  async invia(e) {
    e.preventDefault();
    this.mostra('');
    const errate = this.controlla();
    if (errate.length) {
      this.segnaErrori(errate);
      this.mostra(this.testo('erroreCampi'));
      return;
    }
    this.button.disabled = true;
    this.button.textContent = this.testo('invio');
    this.form.elements.t.value = String(Math.round(performance.now() - this.aperto));
    const dati = new FormData(this.form);
    // il server verifica il CAPTCHA prima di leggere il modulo: il gettone va nell'intestazione
    const token = String(dati.get('cf-turnstile-response') || '');
    let ok = false;
    try {
      const res = await fetch(this.dataset.api, {
        method: 'POST',
        body: dati,
        headers: { 'X-Turnstile-Token': token },
      });
      const esito = await res.json().catch(() => ({}));
      if (res.ok && esito.ok) {
        ok = true;
        this.form.hidden = true;
        this.querySelector('.careers-success').hidden = false;
        this.querySelector('.careers-success').focus();
        return;
      }
      const messaggi = {
        campi_non_validi: 'erroreCampi',
        troppi_invii: 'erroreTroppi',
        captcha: 'erroreCaptcha',
        annuncio_non_disponibile: 'erroreNonDisponibile',
        troppo_grande: 'erroreTroppoGrande',
      };
      this.segnaErrori(esito.errate || []);
      this.mostra(this.testo(messaggi[esito.codice] || 'erroreGenerico'));
    } catch {
      this.mostra(this.testo('erroreGenerico'));
    } finally {
      // il gettone CAPTCHA vale una sola volta: dopo un errore serve un nuovo gettone
      if (!ok) window.turnstile?.reset();
      this.button.disabled = false;
      this.button.textContent = this.testo('invia');
    }
  }
}
customElements.define('careers-form', CareersForm);
